import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { AppDialog } from "../../../../components/common/CustomMdal";
import { updateTeam } from "../../../myTeam/api/myTeamApi";
import { Api } from "../../../../services/interceptor";
import { QUERY_KEY } from "../../../../services/queryEndPoint";
import { resolvePublicImageUrl } from "../../../../helper/helper";
import { normalizeImageFileTypeForApi } from "../../../../helper/imageFileTypeForApi";
import type { ITeamSnapshotResponse } from "../../../myTeam/types/myTeam";
import toast from "react-hot-toast";

function normalizeSnapshotFromApi(raw: unknown): ITeamSnapshotResponse | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const snap = String(r.teamSnapshotId ?? r.teamSnapShotId ?? "").trim();
  if (!snap) return null;
  const base = raw as ITeamSnapshotResponse;
  return { ...base, teamSnapshotId: snap };
}

interface UpdateTeamModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
  /** From tournament team list — used if GET snapshots omit or use alternate id keys. */
  teamSnapshotIdFromList?: string;
  /** Fallback name shown before the API fetch completes. */
  initialData?: {
    teamName?: string;
  };
  onSuccess?: () => void;
}

const UpdateTeamModal = ({
  open,
  onOpenChange,
  teamId,
  teamSnapshotIdFromList = "",
  initialData,
  onSuccess,
}: UpdateTeamModalProps) => {
  const tournamentId = sessionStorage.getItem("tournamentId") ?? "";

  const [teamName, setTeamName] = useState(initialData?.teamName ?? "");
  const [teamCode, setTeamCode] = useState("");
  const [description, setDescription] = useState("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [imageFileType, setImageFileType] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch full team snapshot details when modal opens
  const { data: snapshots, isFetching } = useQuery<ITeamSnapshotResponse[]>({
    queryKey: [QUERY_KEY.teamsSnapShotByTeamId, teamId],
    queryFn: async () => {
      const res = await Api.get(`${QUERY_KEY.teamsSnapShotByTeamId}/${teamId}`);
      const raw = res?.data;
      let list: unknown[] = [];
      if (Array.isArray(raw)) list = raw;
      else if (raw && typeof raw === "object") {
        const obj = raw as Record<string, unknown>;
        const arr = obj.teamSnapshotResponseList ?? obj.teamSnapshots ?? obj.data;
        if (Array.isArray(arr)) list = arr;
      }
      return list
        .map(normalizeSnapshotFromApi)
        .filter((s): s is ITeamSnapshotResponse => s != null);
    },
    enabled: open && !!teamId,
    staleTime: 0,
  });

  // Pick the snapshot for the current tournament (or fall back to first)
  const snapshot: ITeamSnapshotResponse | undefined =
    (tournamentId
      ? snapshots?.find((s) => s.tournament?.tournamentId === tournamentId)
      : undefined) ?? snapshots?.[0];

  const resolvedTeamSnapshotId = (
    snapshot?.teamSnapshotId ??
    teamSnapshotIdFromList ??
    ""
  ).trim();

  // Populate form fields when snapshot data arrives
  useEffect(() => {
    if (snapshot) {
      setTeamName(snapshot.teamName ?? snapshot.teamSnapshotName ?? initialData?.teamName ?? "");
      setTeamCode(snapshot.teamCode ?? "");
      setDescription(snapshot.description ?? "");
      const resolved = resolvePublicImageUrl(snapshot.imageUrl ?? null);
      setImagePreview(resolved || null);
      setImageBase64(null);
      setImageFileType(null);
    }
  }, [snapshot]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset state when modal closes or different team is opened
  useEffect(() => {
    if (!open) {
      setTeamName(initialData?.teamName ?? "");
      setTeamCode("");
      setDescription("");
      setImagePreview(null);
      setImageBase64(null);
      setImageFileType(null);
    }
  }, [open, teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setImagePreview(result);
      setImageBase64(result.split(",")[1] ?? null);
      setImageFileType(normalizeImageFileTypeForApi(file.type, file.name));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamName.trim()) return;
    if (!resolvedTeamSnapshotId) {
      toast.error("Missing team snapshot for this tournament. Try again or refresh the page.");
      return;
    }
    setSubmitting(true);
    try {
      await updateTeam({
        teamId,
        teamSnapshotId: resolvedTeamSnapshotId,
        teamSnapShotId: resolvedTeamSnapshotId,
        teamName: teamName.trim(),
        teamCode: teamCode.trim() || undefined,
        description: description.trim() || undefined,
        ...(imageBase64 && imageFileType
          ? { image: { fileType: imageFileType, base64Image: imageBase64 } }
          : {}),
        captainPlayerId: snapshot?.captainPlayerId ?? undefined,
        viceCaptainPlayerId: snapshot?.viceCaptainPlayerId ?? undefined,
        wkPlayerId: snapshot?.wkPlayerId ?? undefined,
        tournamentId: tournamentId || undefined,
      });
      toast.success("Team updated successfully.");
      onOpenChange(false);
      onSuccess?.();
    } catch {
      toast.error("Failed to update team. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Update Team"
      size="lg"
      className="update-team-dialog"
    >
      {isFetching && !snapshot ? (
        <div className="update-team-loading">Loading team details…</div>
      ) : (
        <form onSubmit={handleSubmit} className="update-team-form">
          <div className="update-team-row">
            <div className="update-team-field">
              <label className="update-team-label">Team Image</label>
              <div
                className="team-image-upload"
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
              >
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Team logo"
                    className="team-image-preview"
                  />
                ) : (
                  <span className="team-image-placeholder">Add image/logo</span>
                )}
                <button
                  type="button"
                  className="upload-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  &#8593; Upload
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
            </div>

            <div className="update-team-field">
              <label className="update-team-label">Team Name*</label>
              <input
                type="text"
                className="update-team-input"
                placeholder="Enter Team Name"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                required
              />
            </div>

            <div className="update-team-field">
              <label className="update-team-label">Team Code</label>
              <input
                type="text"
                className="update-team-input"
                placeholder="Enter Team Code"
                value={teamCode}
                onChange={(e) => setTeamCode(e.target.value)}
              />
            </div>
          </div>

          <div className="update-team-field update-team-desc-field">
            <label className="update-team-label">Team Description</label>
            <textarea
              className="update-team-textarea"
              placeholder="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </div>

          <button
            type="submit"
            className="update-team-submit"
            disabled={submitting || !teamName.trim()}
          >
            {submitting ? "Updating..." : "Update Team"}
          </button>
        </form>
      )}
    </AppDialog>
  );
};

export default UpdateTeamModal;
